import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { useParams, useSearchParams } from 'react-router-dom';

import { Tab } from '@flintmint/web-shared/components/tab';
import { Tabs } from '@flintmint/web-shared/components/tabs';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Button } from '@flintmint/web-shared/components/button';
import { Popover } from '@flintmint/web-shared/components/popover';
import { WorkspaceSidebarList } from '@/features/workspace-sidebar';
import { InitialSyncBanner } from '@/components/initial-sync-banner';
import { StartingPointSelector } from '@/components/starting-point-selector';
import { ProgressiveBlur } from '@flintmint/web-shared/components/progressive-blur';

import { useDocuMenu } from '@/hooks/use-docu-menu';
import { useItemActions } from '@/hooks/use-item-actions';
import { buildDraftUrl } from '@/hooks/use-draft-document';

import { emptyAccountAtom } from '@/state';
import { itemTypes } from '@/constants/item-types';
import { libraryNoDataDescriptions } from '@/constants/ui';
import { defaultSearchableTypes, libraryTypeFilters, searchFilterKey, typeFilterKey } from '@/constants/search-filters';

import styles from './library.module.scss';

export const Library = () => {
  const [openStartingPoint, setOpenStartingPoint] = useState(false);

  const isEmptyAccount = useAtomValue(emptyAccountAtom);

  const { id: activeDocumentId } = useParams();
  const [searchParams] = useSearchParams();

  const menuData = useDocuMenu();
  const { createNew } = useItemActions();

  const menu = [
    {
      icon: 'note-01',
      name: 'New Note',
      'aria-label': 'Create new note',
      onClick: () => createNew({ type: 'notes' }),
      link: buildDraftUrl({ type: 'notes', preserveSearchParams: searchParams }),
    },
    {
      icon: 'ai-magic',
      name: 'New Medical Note',
      'aria-label': 'Create new medical note',
      onClick: () => createNew({ type: 'notes', domain: 'medical' }),
      link: buildDraftUrl({ type: 'notes', domain: 'medical', preserveSearchParams: searchParams }),
    },
    {
      icon: 'note',
      name: 'New Template',
      'aria-label': 'Create new template',
      onClick: () => createNew({ type: 'templates' }),
      link: buildDraftUrl({ type: 'templates', preserveSearchParams: searchParams }),
    },
    {
      name: 'New Block',
      icon: 'blockchain-01',
      'aria-label': 'Create new block',
      onClick: () => createNew({ type: 'blocks' }),
    },
  ];

  return (
    <>
      <WorkspaceSidebarList
        searchParamKey={searchFilterKey}
        filterParamKey={typeFilterKey}
        defaultFilterValue="all"
        getSearchableTypes={({ filterValue }) => {
          const typeId = filterValue || 'all';
          const searchAllTypes = typeId === 'all';
          const type = libraryTypeFilters.find((el) => el.id === typeId);
          return type && !searchAllTypes ? [type.id] : defaultSearchableTypes;
        }}
        getNoDataConfig={({ filterValue, hasSearchValue }) => {
          const typeId = (filterValue as keyof typeof itemTypes | 'all') || 'all';
          const searchAllTypes = typeId === 'all';
          const targetType: keyof typeof itemTypes = searchAllTypes ? 'notes' : (typeId as keyof typeof itemTypes);

          return {
            icon: 'note-bulk',
            descriptions: libraryNoDataDescriptions,
            defaultActions: [
              {
                icon: 'plus',
                label: `New ${itemTypes[targetType].name}`,
                onClick: () => {
                  if (targetType === 'notes' && (isEmptyAccount || !hasSearchValue)) {
                    setOpenStartingPoint(true);
                  } else {
                    createNew({ type: targetType });
                  }
                },
              },
            ],
          };
        }}
        renderFilters={({ filterValue, setFilterValue }) => {
          const typeId = filterValue || 'all';

          return (
            <Tabs
              value={typeId}
              appearance="simple"
              onChange={(value) => setFilterValue?.(value)}
              className={styles.libraryFilters}
            >
              {libraryTypeFilters.map(({ name, id }) => (
                <Tab id={id} key={id} name={name} role="listitem" />
              ))}
            </Tabs>
          );
        }}
        renderActions={() => (
          <Popover menu={menu} placement="bottom">
            <Icon size={20} type="plus" data-testid="left-sidebar-plus-btn" />
          </Popover>
        )}
        docuListProps={{
          menu: menuData,
          'data-testid': 'library',
          selections: [],
          activeDocumentId,
          searchableTypes: defaultSearchableTypes,
          listHeader: <InitialSyncBanner />,
          bottomActions: (
            <div className={styles.newNoteAction}>
              <ProgressiveBlur position="bottom" intensity={40} />
              <Button
                size="big"
                color="dark"
                icon={<Icon type="plus" size={20} />}
                onClick={() => setOpenStartingPoint(true)}
                data-testid="bottom-actions-new-note-btn"
              >
                New Note
              </Button>
            </div>
          ),
        }}
      />
      <StartingPointSelector modal open={openStartingPoint} onClose={() => setOpenStartingPoint(false)} />
    </>
  );
};
